/**
 * BriefsPage — data-bound Briefs tab (Phase 4 Plan 08 Task 3).
 *
 * Layout mirrors IA §6.1 (current widgets — complexity row removed in v1.4
 * because scheduled briefs don't carry DST complexity tags and the row
 * collapsed to a single "unknown" bucket):
 *   Row 1 — KPI grid: Sent | Delivery rate | Avg rating | Skipped %
 *   Row 2 — Delivery trend (stacked area, 3 series sent/failed/skipped)
 *   Row 3 (2-col on xl) — Per-user status table (2/3) + Failure donut (1/3)
 *
 * BLOCKER-06 locks — verified at every widget:
 *   - BriefStatus enum is exactly sent / failed / skipped. The trend chart
 *     has EXACTLY 3 <Area> entries; no fourth legacy activity series.
 *   - BriefsKpis uses sent (not the old delivered synonym); trend uses the
 *     same key set.
 *   - BriefComplexity enum is simple/medium/complex/unknown (sync.ts:57).
 *
 * Every row is wrapped in a <TabErrorBoundary> so one render failure
 * doesn't take down the tab. Each hook owns its own loading / error
 * handling — error states render an inline retry card matching the
 * convention from OperationsPage / EngagementPage / QualityPage.
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  useBriefLog,
  useBriefTrend,
  useBriefsKpis,
} from '@/hooks/use-briefs';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { readTableSort, resolveRange, type TableSort } from '@/lib/filters-schema';
import { effectiveGranularity } from '@/lib/granularity';

import {
  BRIEF_LOG_TABLE_ID,
  BriefLogTable,
  briefLogRowKey,
} from '@/components/BriefLogTable';
import { BriefMessageViewer } from '@/components/BriefMessageViewer';
import { KPICard } from '@/components/KPICard';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { ChartTooltip, useChartTooltipTrigger } from '@/components/charts/ChartTooltip';
import {
  TimelineTotalsBadge,
  sumField,
  type TimelineTotalItem,
} from '@/components/charts/TimelineTotalsBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type {
  BriefLogRow,
  BriefStatus,
  BriefTrendPoint,
} from '@/services/api-types';

// ---------------------------------------------------------------------------
// Inline retry card — mirrors OperationsPage's ErrorRetry
// ---------------------------------------------------------------------------

function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Delivery trend chart — IA §6.1.2
// ---------------------------------------------------------------------------

// P12 — delegate to central formatter so hourly buckets include day+time.
import { formatBucketLabel as formatBucketLabelShared } from '@/lib/format-bucket';
function formatBucketLabel(bucket: string): string {
  return formatBucketLabelShared(bucket, 'auto');
}

// 3 series only — BLOCKER-06. Colours match BriefStatusTable chips and
// BriefComplexitySankey segments.
//
// Delivery-trend uses deeper, more saturated hex than the semantic 400-level
// tokens (positive/negative/warning). The three series are all stacked on a
// single chart and need to read as crisp states — a lit green for "sent",
// a strong red for "failed", a vivid amber for "skipped". The semantic
// tokens stay 400-level for charts where they share the surface with grid
// lines and muted actors; here the chart IS the story, so it gets the
// louder palette the user signed off on in pass-1.
const TREND_SERIES: ReadonlyArray<{
  key: 'sent' | 'failed' | 'skipped';
  label: string;
  color: string;
}> = [
  { key: 'sent', label: 'Sent', color: '#22c55e' }, // green-500
  { key: 'failed', label: 'Failed', color: '#dc2626' }, // red-600
  { key: 'skipped', label: 'Skipped', color: '#f59e0b' }, // amber-500
];

type TrendSeriesKey = 'sent' | 'failed' | 'skipped';

function DeliveryTrendChart({
  data,
  loading,
  height = 280,
}: {
  data: BriefTrendPoint[];
  loading: boolean;
  height?: number;
}): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  // P2-1 — legend click toggles series visibility. Stored as a Set of
  // hidden keys; Recharts' Legend `onClick` fires with `{ dataKey }` —
  // we flip that key in/out of the hidden set. Hidden series render with
  // fill/stroke transparency so the legend label stays readable.
  const [hidden, setHidden] = React.useState<ReadonlySet<TrendSeriesKey>>(
    () => new Set(),
  );
  // Recharts typings declare the Legend onClick payload as `{ dataKey?:
  // DataKey<any> }` where DataKey may be a string, number, or accessor
  // function. We only dispatch on string keys, so we accept `unknown` and
  // narrow explicitly — prevents a TS2769 without dragging the internal
  // Payload type import (which is deep-pathed under recharts/types/).
  const handleLegendClick = React.useCallback((entry: unknown) => {
    const key = (entry as { dataKey?: unknown } | undefined)?.dataKey;
    if (key !== 'sent' && key !== 'failed' && key !== 'skipped') return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  if (loading) {
    return (
      <div
        data-testid="brief-trend-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        data-testid="brief-trend-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No briefs in selected period
      </div>
    );
  }
  return (
    // P1-5 — explicit `w-full` + `min-w-0` so the chart always spans the
    // CardContent's available width. Inline `width: 100%` alone wasn't
    // enough on mobile where recharts' first measurement returned a
    // container that hadn't yet reached full width.
    <div
      data-testid="brief-trend"
      className="w-full min-w-0"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height={height} minWidth={0}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucketLabel(v)}
          />
          <YAxis allowDecimals={false} />
          {/* bug F1 — themed tooltip. Mobile-tap persistence via trigger switch. */}
          <Tooltip
            {...tooltipTrigger}
            cursor={false}
            content={
              <ChartTooltip
                labelFormatter={(v) =>
                  typeof v === 'string' ? formatBucketLabel(v) : String(v ?? '')
                }
              />
            }
          />
          <Legend
            onClick={handleLegendClick}
            formatter={(value, entry) => {
              const key = (entry?.dataKey as TrendSeriesKey | undefined) ?? null;
              const isHidden = key ? hidden.has(key) : false;
              return (
                <span
                  style={{
                    cursor: 'pointer',
                    opacity: isHidden ? 0.4 : 1,
                    textDecoration: isHidden ? 'line-through' : 'none',
                  }}
                >
                  {value}
                </span>
              );
            }}
          />
          {TREND_SERIES.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stackId="1"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.75}
              hide={hidden.has(s.key)}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-brief log section (IA §6.1.3, post-v1.5) — one row per send, with
// pagination and inline brief-content expansion.
// ---------------------------------------------------------------------------

/** Default page size — 25 rows fit in ~1 viewport without scrolling on desktop
 *  and ~2–3 viewports on mobile. Readers see newest sends first (the table
 *  sorts by `sentAt desc` by default) and extend via "Show more". */
const BRIEF_LOG_PAGE_SIZE = 25;

/** Default sort — mirrors `defaultSort` on `<BriefLogTable>` so the URL-less
 *  initial render orders rows the same way the table header arrows indicate. */
const BRIEF_LOG_DEFAULT_SORT: TableSort = {
  column: 'sentAt',
  dir: 'desc',
};

/**
 * Triage-priority weights for the Status column — failed/skipped surface
 * before sent on desc, so a single "Status desc" click puts the rows that
 * need attention at the top.
 */
const STATUS_PRIORITY: Record<BriefStatus, number> = {
  failed: 3,
  skipped: 2,
  sent: 1,
};

/**
 * Client-side comparator for the brief-log table.
 *
 * `SortableTable` only writes `sort[briefLog]` to the URL on header clicks —
 * it does not reorder the `rows` prop. Same pattern as OperationsPage:
 * read the URL sort here, apply a column-aware comparator, then slice for
 * pagination.
 *
 * Per-column behavior:
 *   - `sentAt` : ISO timestamp. Compare via `Date.parse()`.
 *   - `status` : triage priority (see STATUS_PRIORITY).
 *   - `rating` : nullable number 0..5. Null sinks to BOTTOM regardless of
 *     direction — an unrated brief shouldn't appear above a 1.0/5 row on asc.
 *
 * Ties break on `traceId` (asc) so the ordering is stable across renders —
 * matters for pagination: without a tiebreak, "Show more" could shuffle
 * previously-visible rows into the second page.
 */
function sortBriefLogRows(
  rows: BriefLogRow[],
  sort: TableSort,
): BriefLogRow[] {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    let primary = 0;
    if (sort.column === 'sentAt') {
      primary = (Date.parse(a.sentAt) - Date.parse(b.sentAt)) * dir;
    } else if (sort.column === 'kind') {
      // QA 2026-06-19: the Kind column is sortable and the log mixes several
      // kinds (scheduled_brief / mini_brief / onboarding_message /
      // brief_command), but there was no comparator branch — clicking the
      // header was a no-op. Sort by the raw kind string.
      primary = a.kind.localeCompare(b.kind) * dir;
    } else if (sort.column === 'status') {
      primary = (STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status]) * dir;
    } else if (sort.column === 'rating') {
      const aMissing = a.rating === null;
      const bMissing = b.rating === null;
      if (aMissing && bMissing) primary = 0;
      else if (aMissing) primary = 1; // null sinks to bottom regardless of dir
      else if (bMissing) primary = -1;
      else primary = ((a.rating as number) - (b.rating as number)) * dir;
    }
    if (primary !== 0) return primary;
    // Tiebreak on traceId for stable ordering across renders.
    return a.traceId.localeCompare(b.traceId);
  });
}

// ---------------------------------------------------------------------------
// Inline row-expand URL state (Phase 8)
// ---------------------------------------------------------------------------

/** URL param name carrying expanded row keys ("user:date" comma-separated). */
const EXPANDED_PARAM = 'expanded';

/**
 * Parse the `?expanded=` URL param into a Set of expansion keys. The encoded
 * format is `user:YYYY-MM-DD:kind,user2:YYYY-MM-DD:kind2` (kind = raw trace
 * name) so a copied URL deep-links the exact rows the previous viewer had open.
 * The `:kind` suffix disambiguates a morning brief and an onboarding message
 * sent to the same RM on the same UTC day (see `briefLogRowKey`).
 *
 * Filter out any malformed entry (missing colon, empty user, bad date, missing
 * kind) so a hand-edited URL can't crash the renderer — silently dropping gives
 * a graceful degradation instead of an "Invalid URL" full-page error.
 */
function parseExpanded(raw: string | null): ReadonlySet<string> {
  if (!raw) return new Set<string>();
  const parts = raw.split(',');
  const out = new Set<string>();
  for (const part of parts) {
    // Split into [user, date, kind] — kind may itself be empty-string-safe
    // but must be present so the key round-trips `briefLogRowKey`.
    const segs = part.split(':');
    if (segs.length < 3) continue;
    const [user, date, ...kindSegs] = segs;
    const kind = kindSegs.join(':');
    if (!user || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !kind) continue;
    out.add(`${user}:${date}:${kind}`);
  }
  return out;
}

function BriefLogSection({
  rows,
  isLoading,
}: {
  rows: BriefLogRow[];
  isLoading: boolean;
}): React.ReactElement {
  const { filters } = useUrlFilters();
  const sort =
    readTableSort(filters, BRIEF_LOG_TABLE_ID) ?? BRIEF_LOG_DEFAULT_SORT;

  // URL-synced expansion. Writes go through `useSearchParams` directly
  // because `useUrlFilters` only mirrors schema-owned keys (its merge step
  // strips anything else). Writes preserve every other URL key so global
  // filters / sort / sibling deep links survive a row toggle.
  const [searchParams, setSearchParams] = useSearchParams();
  // URL set carries `user:date:kind` (UTC) — same format `briefLogRowKey`
  // produces, so the SortableTable's `expandedKeys` lookup matches without
  // projection.
  const expandedKeys = React.useMemo(
    () => parseExpanded(searchParams.get(EXPANDED_PARAM)),
    [searchParams],
  );

  const toggleExpanded = React.useCallback(
    (row: BriefLogRow) => {
      const key = briefLogRowKey(row);
      // briefLogRowKey returns `${userId}:invalid:${traceId}` for malformed
      // sentAt — treat that as non-expandable rather than poison the URL.
      if (key.includes(':invalid:')) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const current = parseExpanded(next.get(EXPANDED_PARAM));
          const updated = new Set(current);
          if (updated.has(key)) updated.delete(key);
          else updated.add(key);
          if (updated.size === 0) {
            next.delete(EXPANDED_PARAM);
          } else {
            next.set(EXPANDED_PARAM, Array.from(updated).join(','));
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const renderExpanded = React.useCallback(
    (row: BriefLogRow): React.ReactNode => {
      const key = briefLogRowKey(row);
      if (key.includes(':invalid:')) return null;
      const [userId, date] = key.split(':') as [string, string];
      return (
        <BriefMessageViewer
          userId={userId}
          date={date}
          fallbackTraceId={row.traceId}
        />
      );
    },
    [],
  );

  const totalCount = rows.length;

  // Sort BEFORE paginating — otherwise page 1 would show 25 arbitrary rows
  // and "Show more" would reveal the ordering the user asked for. See
  // `sortBriefLogRows` for the comparator details.
  const sortedRows = React.useMemo(
    () => sortBriefLogRows(rows, sort),
    [rows, sort],
  );

  // Page-size pagination. Store page count (how many pages are visible),
  // not page index — "Show more" extends the visible slice.
  const [visiblePages, setVisiblePages] = React.useState(1);

  // Reset page count when the dataset swaps underneath us (filter change
  // that adds/removes rows).
  React.useEffect(() => {
    setVisiblePages(1);
  }, [totalCount]);

  // Reset to page 1 on sort change too — otherwise switching sort while on
  // page 3 would show rows 51..75 of the new ordering.
  React.useEffect(() => {
    setVisiblePages(1);
  }, [sort.column, sort.dir]);

  const visibleLimit = visiblePages * BRIEF_LOG_PAGE_SIZE;
  const visibleRows = React.useMemo(
    () => sortedRows.slice(0, visibleLimit),
    [sortedRows, visibleLimit],
  );
  const remaining = Math.max(0, totalCount - visibleLimit);

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground">
        Briefs in selected period
      </div>
      <BriefLogTable
        rows={visibleRows}
        isLoading={isLoading}
        renderExpanded={renderExpanded}
        expandedKeys={expandedKeys}
        onToggleExpanded={toggleExpanded}
      />
      {!isLoading && totalCount > 0 && (
        <div className="flex items-center justify-between gap-3 pt-1 text-xs text-muted-foreground">
          <span data-testid="brief-log-count">
            Showing {visibleRows.length} of {totalCount} briefs
          </span>
          {remaining > 0 && (
            <button
              type="button"
              data-testid="brief-log-show-more"
              onClick={() => setVisiblePages((n) => n + 1)}
              className="underline text-primary hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded px-1"
            >
              Show {Math.min(remaining, BRIEF_LOG_PAGE_SIZE)} more
              {' '}({remaining} left)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function BriefsPage(): React.ReactElement {
  const { filters } = useUrlFilters();

  // Keep effective granularity computation on the client for parity with
  // other tabs; the server's auto-rule is the source of truth for data
  // shape, so this is informational (future: render the subbar hint).
  const range = resolveRange(filters);
  const _eff = effectiveGranularity(filters.granularity, range.from, range.to);
  void _eff;

  const kpis = useBriefsKpis(filters);
  const trend = useBriefTrend(filters, filters.granularity);
  const briefLog = useBriefLog(filters);
  const k = kpis.data;

  // deliveryRate + skippedPct are 0..1 from the backend; show as percent.
  const deliveryPct =
    k === undefined ? null : Number((k.deliveryRate.current * 100).toFixed(1));
  const skippedPct =
    k === undefined ? null : Number((k.skippedPct.current * 100).toFixed(1));
  // Avg rating is nullable (null when no rated briefs in range).
  const avgRatingValue = k?.avgRating.current ?? null;

  // New fields (no deltas — raw counts only). Older API responses / empty dev
  // data may omit `onboarding` / `skipReasons` entirely, so every read is
  // optional-chained to 0 — render zeros, never crash.
  const onboarding = k?.onboarding;
  const skipReasons = k?.skipReasons;
  const skipDayOff = skipReasons?.dayOff ?? 0;
  const skipEmpty = skipReasons?.empty ?? 0;

  return (
    <section aria-labelledby="briefs-heading" className="space-y-6">
      <header>
        <h1 id="briefs-heading" className="text-2xl font-semibold">
          Briefs
        </h1>
        <p className="text-sm text-muted-foreground">
          Pipeline health for scheduled morning briefs — delivery, failures,
          and complexity mix.
        </p>
        <RecountNotice queries={[kpis, trend, briefLog]} />
      </header>

      {/* Row 1 — KPI grid (IA §6.1.1). 4 cards on desktop. */}
      <TabErrorBoundary name="KPI row">
        {kpis.isError ? (
          <ErrorRetry label="KPIs" onRetry={() => void kpis.refetch()} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
            <KPICard
              label="Sent"
              value={k?.sent.current ?? null}
              delta={k?.sent.delta ?? null}
              deltaCurrent={k?.sent.current ?? null}
              deltaPrevious={k?.sent.previous ?? null}
              loading={kpis.isLoading}
              tooltip="Number of scheduled morning briefs successfully delivered to Slack in the window."
            />
            <KPICard
              label="Delivery rate"
              value={deliveryPct}
              format="percent"
              delta={k?.deliveryRate.delta ?? null}
              // Scale 0..1 → 0..100 so the delta-pill tooltip reads in the same
              // units as the headline value ("82.0%", not "0.8%"). QA 2026-06-19
              // — matches OperationsPage's ratio-metric pattern.
              deltaCurrent={k == null ? null : k.deliveryRate.current * 100}
              deltaPrevious={k == null ? null : k.deliveryRate.previous * 100}
              loading={kpis.isLoading}
              tooltip="sent / (sent + failed + skipped). Skipped briefs count toward the denominator — they represent delivery failures at the scheduler layer."
            />
            <KPICard
              label="Avg rating"
              value={avgRatingValue}
              format="rating"
              delta={k?.avgRating.delta ?? null}
              deltaCurrent={k?.avgRating.current ?? null}
              deltaPrevious={k?.avgRating.previous ?? null}
              loading={kpis.isLoading}
              tooltip="Mean star rating on briefs that received feedback (1–5)."
            />
            <KPICard
              label="Skipped"
              value={skippedPct}
              format="percent"
              /* Skip-reason split — raw counts, no delta. Renders "Day off N ·
                 Empty N" under the headline %; absent fields fall back to 0. */
              /* Built from `?? 0`, so this string is NEVER empty and would
                 print "Day off 0 · Empty 0" over a window nobody has counted
                 if KPICard's own guard ever moved. Gated on the payload, like
                 the other thirteen sublabels on this dashboard. */
              subLabel={
                skipReasons
                  ? `Day off ${skipDayOff} · Empty ${skipEmpty}`
                  : undefined
              }
              delta={k?.skippedPct.delta ?? null}
              // Scale 0..1 → 0..100 so the delta-pill tooltip matches the % value.
              deltaCurrent={k == null ? null : k.skippedPct.current * 100}
              deltaPrevious={k == null ? null : k.skippedPct.previous * 100}
              /* Higher skip rate is worse — invert the delta colour. */
              inverted
              loading={kpis.isLoading}
              tooltip="% of scheduled briefs skipped (no data, off-day, user opted out). Rising % means the brief isn't finding anything useful to say. Day off = scheduled non-working day; Empty = no activity to report."
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* Onboarding breakdown — compact card under the KPI row. Onboarding
          messages are a distinct scheduled-delivery kind; their sent/failed/
          skipped counts are ALSO folded into the KPI row above, so this card
          isolates the subset. Raw counts only — no deltas. Reuses
          TimelineTotalsBadge (same swatch+label:value block the trend header
          uses) so the styling matches the rest of the tab 1:1. Absent fields
          render as 0 via optional-chaining; the card always shows. */}
      <TabErrorBoundary name="Onboarding breakdown">
        <Card data-testid="brief-onboarding">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Onboarding messages
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <TimelineTotalsBadge
              data-testid="brief-onboarding-totals"
              loading={kpis.isLoading}
              items={[
                {
                  key: 'sent',
                  label: 'Sent',
                  color: '#22c55e',
                  value: onboarding?.sent ?? 0,
                },
                {
                  key: 'failed',
                  label: 'Failed',
                  color: '#dc2626',
                  value: onboarding?.failed ?? 0,
                },
                {
                  key: 'skipped',
                  label: 'Skipped',
                  color: '#f59e0b',
                  value: onboarding?.skipped ?? 0,
                },
              ]}
            />
          </CardContent>
        </Card>
      </TabErrorBoundary>

      {/* Row 2 — Delivery trend (stacked, 3 series). IA §6.1.2. */}
      <TabErrorBoundary name="Delivery trend">
        {trend.isError ? (
          <ErrorRetry
            label="Delivery trend"
            onRetry={() => void trend.refetch()}
          />
        ) : (
          <Card>
            <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Delivery trend (sent / failed / skipped)
              </CardTitle>
              <TimelineTotalsBadge
                data-testid="brief-trend-totals"
                loading={trend.isLoading}
                items={TREND_SERIES.map<TimelineTotalItem>((s) => ({
                  key: s.key,
                  label: s.label,
                  color: s.color,
                  value: sumField(trend.data ?? [], s.key),
                }))}
              />
            </CardHeader>
            <CardContent className="p-4">
              <DeliveryTrendChart
                data={trend.data ?? []}
                loading={trend.isLoading}
              />
            </CardContent>
          </Card>
        )}
      </TabErrorBoundary>

      {/* Row 3 — Per-brief log (full width). One row per send in the
          selected window, sorted by sentAt desc by default. */}
      <TabErrorBoundary name="Brief log">
        {briefLog.isError ? (
          <ErrorRetry
            label="Brief log"
            onRetry={() => void briefLog.refetch()}
          />
        ) : (
          <BriefLogSection
            rows={briefLog.data ?? []}
            isLoading={briefLog.isLoading}
          />
        )}
      </TabErrorBoundary>

      {/* Complexity × status widget removed — scheduled briefs don't carry
          DST complexity tags, so the row collapsed to a single "unknown"
          bucket and added no signal on top of the trend + per-user table. */}
    </section>
  );
}
